import { ref, computed, watch } from "@vue/composition-api";
import store from "@/store";

export default function useCalendarEventHandler(
  props,
  clearForm,
  emit,
  attachments = ref([]),
  refFormObserver
) {
  // ------------------------------------------------
  // eventLocal
  // ------------------------------------------------
  const normalizeExtendedProps = (evt) => {
    if (!evt) return evt;
    if (!evt.extendedProps) evt.extendedProps = {};
    const ep = evt.extendedProps;
    // Map legacy keys
    if (!ep.eventType && ep.type) ep.eventType = ep.type;
    if (ep.uploadToAllTeachers === undefined && ep.adjustPlanner !== undefined) {
      ep.uploadToAllTeachers = Boolean(ep.adjustPlanner);
    }
    // Defaults
    if (!ep.eventType) ep.eventType = 'event';
    if (ep.uploadToAllTeachers === undefined) ep.uploadToAllTeachers = false;
    return evt;
  };

  const eventLocal = ref(normalizeExtendedProps(JSON.parse(JSON.stringify(props.event.value))));
  const resetEventLocal = () => {
    const fresh = JSON.parse(JSON.stringify(props.event.value));
    eventLocal.value = normalizeExtendedProps(fresh);
    refFormObserver.value.reset();
  };
  watch(props.event, () => {
    resetEventLocal();
  });

  // ------------------------------------------------
  // isEventHandlerSidebarActive
  // * Clear form if sidebar is closed
  // ------------------------------------------------
  watch(props.isEventHandlerSidebarActive, (val) => {
    // ? Don't reset event till transition is finished
    if (!val) {
      setTimeout(() => {
        clearForm.value();
      }, 350);
    }
  });

  // ------------------------------------------------
  // calendarOptions
  // ------------------------------------------------
  const calendarOptions = computed(() => store.state.calendar.calendarOptions);

  const onSubmit = async () => {
    try {
      console.log("onSubmit called - preparing event data");

      // Helper functions to convert arrays to comma-separated strings
      const arrayToString = (arr) => {
        if (!arr || arr.length === 0) return "";
        // If array contains 0 (All), return empty string
        if (arr.includes(0)) return "";
        return arr.join(",");
      };

      // Helper function to format date as YYYY-MM-DD only (no time)

      // Prepare the event data with safe property access
      const extendedProps = eventLocal.value.extendedProps || {};
      
      // Handle calendar as array or string - convert to comma-separated string if array
      let eventFor = eventLocal.value.extendedProps.calendar;
      if (Array.isArray(eventFor)) {
        eventFor = eventFor.join(",");
      }
      
      const eventData = {
        id: Number(eventLocal.value.id) || 0,
        title: eventLocal.value.title,
        start: eventLocal.value.start,
        end: eventLocal.value.end,
        eventFor: eventFor, // Students, Staff, or "Students,Staff" for both
        campusID: store.state.userData.cId,
        clsIDs: arrayToString(eventLocal.value.extendedProps.classID),
        secIDs: arrayToString(eventLocal.value.extendedProps.secID),
        departmentIDs: arrayToString(
          eventLocal.value.extendedProps.departmentID
        ),
        startTime: eventLocal.value.extendedProps.startTime || "",
        endTime: eventLocal.value.extendedProps.endTime || "",
        location: eventLocal.value.extendedProps.location || "",
        locationLink: eventLocal.value.extendedProps.eventLink || "",
        description: eventLocal.value.extendedProps.description || "",
        // Newly added fields to pass event type and holiday adjustment state
        eventType: extendedProps.eventType || "event",
        uploadToAllTeachers: Boolean(extendedProps.uploadToAllTeachers),
        // API explicit keys (requested):
        adjustPlanner: Boolean(extendedProps.uploadToAllTeachers),
        isPublic: true,
        isFeatured: false,
        status: "active",
        organizer: "",
        targetAudience: "",
        registrationURL: "",
        maxParticipants: 0,
        requiresRegistration: false,
        contactPerson: "",
        contactEmail: "",
        contactPhone: eventLocal.value.extendedProps.contactPhone || "",
      };

      // Prepare attachments data
      const attachmentsData = attachments.value.map((file, index) => ({
        id: file.id || 0,
        eventID: Number(eventLocal.value.id) || 0,
        fileSize: Number(file.fileSize || 0),
        type: file.type,
        url: file.url,
        name: file.name,
        sequence: index + 1,
        isFeatured: false,
      }));

      // Prepare the final payload
      const payload = {
        events: eventData,
        attachments: attachmentsData,
      };

      // Make API call - using the same pattern as resource.vue
      const obj = {
        url:
          store.state.domain +
          "EventsCalendar/Save?db=" +
          store.state.userData.db +
          "&cID=" +
          store.state.userData.cId +
          "&userID=" +
          store.state.userData.userID,
        body: payload,
        token: store.state.userData.token,
        subjects: true,
      };

      const response = await store.dispatch("post", obj);

      if (response) {
        // Clear form and emit success
        clearForm.value();
        emit("refetch-events");
        emit("update:is-event-handler-sidebar-active", false);

        // Show success message
        console.log("Event saved successfully, calendar will refresh");
      }
    } catch (error) {
      console.error("Error saving event:", error);
    }
  };

  // *===============================================---*
  // *--------- UI ---------------------------------------*
  // *===============================================---*

  // ------------------------------------------------
  // guestOptions
  // ------------------------------------------------

  /* eslint-disable global-require */
  const guestsOptions = [
    {
      avatar: require("@/assets/images/avatars/1-small.png"),
      name: "Jane Foster",
    },
    {
      avatar: require("@/assets/images/avatars/3-small.png"),
      name: "Donna Frank",
    },
    {
      avatar: require("@/assets/images/avatars/5-small.png"),
      name: "Gabrielle Robertson",
    },
    {
      avatar: require("@/assets/images/avatars/7-small.png"),
      name: "Lori Spears",
    },
    {
      avatar: require("@/assets/images/avatars/9-small.png"),
      name: "Sandy Vega",
    },
    {
      avatar: require("@/assets/images/avatars/11-small.png"),
      name: "Cheryl May",
    },
  ];
  /* eslint-enable global-require */

  return {
    eventLocal,
    resetEventLocal,
    calendarOptions,

    // UI
    guestsOptions,
    onSubmit,
  };
}
